/**
 * What ops.trav.gg's worker serves at /api/now.json, and the page reads.
 *
 * Built at the edge from the resort's own lift and run report. The resort
 * itself is never named; lift, run and restaurant names are.
 */

export type Grade = 'green' | 'blue' | 'black' | 'double'

export interface Lift {
  id: number
  name: string
  open: boolean
  /** The report's own word when it is neither plain open nor closed ("Wind Hold"). */
  status?: string
}

export interface Run {
  id: number
  name: string
  /** Absent when the report gives a difficulty this page doesn't know. */
  grade?: Grade
  open: boolean
  groomed: boolean
  status?: string
}

export interface Area {
  name: string
  lifts: Lift[]
  runs: Run[]
}

export interface Park {
  open: boolean
  /** Jumps, boxes, rails and other features, as reported. */
  features?: number
  note?: string
}

export interface Restaurant {
  name: string
  open: boolean
  onMountain: boolean
}

export interface OpsPayload {
  version: 1
  /** When the edge last read the report. */
  checkedAt: string
  /** Publication time in the source feed; never substituted with fetch time. */
  publishedAt?: string
  /** When any lift, run, park or restaurant last changed state, as far as the edge has seen. */
  changedAt: string
  resort: {
    /** Open means the report says so and at least one lift is turning. */
    status: 'open' | 'closed'
    /** Local Mountain time, 24 h ("09:00"). */
    opens?: string
    closes?: string
    /** Opening day (YYYY-MM-DD) when known; set in the worker's config. */
    opening?: string
    /** The report's one-line note, e.g. "Closed for the 2026/27 winter season." */
    note?: string
  }
  /** In the report's own order. */
  areas: Area[]
  parks?: { terrain?: Park; tube?: Park }
  dining?: Restaurant[]
}

const object = (v: unknown): v is Record<string, unknown> =>
  v != null && typeof v === 'object' && !Array.isArray(v)
const date = (v: unknown): v is string => typeof v === 'string' && Number.isFinite(Date.parse(v))
const text = (v: unknown): v is string => typeof v === 'string' && v.length > 0
const optional = (v: unknown, test: (v: unknown) => boolean) => v === undefined || test(v)
const localHour = (v: unknown): v is string =>
  typeof v === 'string' && /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(v)
const inventoryId = (v: unknown): v is number => Number.isSafeInteger(v) && Number(v) >= 0
const GRADES = new Set<unknown>(['green', 'blue', 'black', 'double'])

/** Shared by the worker (before it caches a copy) and the page (before it keeps one). */
export function validOps(value: unknown): value is OpsPayload {
  if (!object(value) || value.version !== 1) return false
  if (!date(value.checkedAt) || !date(value.changedAt)) return false
  if (!optional(value.publishedAt, date)) return false
  const resort = value.resort
  if (!object(resort) || (resort.status !== 'open' && resort.status !== 'closed')) return false
  if (
    !optional(resort.opens, localHour) ||
    !optional(resort.closes, localHour) ||
    !optional(resort.note, (v) => typeof v === 'string') ||
    !optional(resort.opening, (v) => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v))
  )
    return false
  if (!Array.isArray(value.areas) || value.areas.length === 0) return false
  return value.areas.every(
    (area: unknown) =>
      object(area) &&
      text(area.name) &&
      Array.isArray(area.lifts) &&
      Array.isArray(area.runs) &&
      area.lifts.every(
        (l: unknown) =>
          object(l) &&
          inventoryId(l.id) &&
          text(l.name) &&
          typeof l.open === 'boolean' &&
          optional(l.status, (s) => typeof s === 'string'),
      ) &&
      area.runs.every(
        (r: unknown) =>
          object(r) &&
          inventoryId(r.id) &&
          text(r.name) &&
          typeof r.open === 'boolean' &&
          typeof r.groomed === 'boolean' &&
          optional(r.grade, (g) => GRADES.has(g)) &&
          optional(r.status, (s) => typeof s === 'string'),
      ),
  )
}
